/**
 * Net — thin browser WebSocket client for the Weird Kart Racer wire protocol.
 *
 * Wraps the native WebSocket, decodes incoming ServerMessages, and dispatches
 * them to type-scoped handlers. It also tracks `playerId` (assigned from the
 * first `joined` message) and `connected` state, and provides typed convenience
 * senders for every ClientMessage.
 */
import {
  BotSkill,
  BotState,
  ClientMessage,
  ServerMessage,
  ServerMessageType,
  ServerMessageOf,
  encode,
  decode,
} from '../shared/protocol';

/** Generic handler for a server message narrowed by its `type`. */
type Handler<T extends ServerMessageType> = (msg: ServerMessageOf<T>) => void;
/** Type-erased handler used for internal storage. */
type AnyHandler = (msg: ServerMessage) => void;

export class Net {
  /** Set automatically when a "joined" message arrives, before dispatching it. */
  playerId: string | null = null;
  /** True while the underlying socket is open. */
  connected = false;

  private ws: WebSocket | null = null;
  /** type -> set of handlers registered for that server message type. */
  private handlers = new Map<ServerMessageType, Set<AnyHandler>>();
  private closeHandlers = new Set<() => void>();

  /** Open a socket to `url`; resolve on open, reject on error/close-before-open. */
  connect(url: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let opened = false;
      let settled = false;

      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
        return;
      }
      this.ws = ws;

      ws.onopen = () => {
        opened = true;
        this.connected = true;
        if (!settled) {
          settled = true;
          resolve();
        }
      };

      ws.onmessage = (ev: MessageEvent) => {
        // Only strings are expected on the wire (JSON). Ignore anything else.
        if (typeof ev.data !== 'string') return;
        this.dispatch(ev.data);
      };

      ws.onerror = () => {
        // Only surface as a connect rejection if we never opened.
        if (!opened && !settled) {
          settled = true;
          reject(new Error('WebSocket error before open'));
        }
      };

      ws.onclose = () => {
        this.connected = false;
        // Ignore stale close events from a superseded socket.
        if (this.ws === ws) this.ws = null;
        for (const h of this.closeHandlers) {
          try {
            h();
          } catch {
            /* swallow handler errors */
          }
        }
        if (!opened && !settled) {
          settled = true;
          reject(new Error('WebSocket closed before open'));
        }
      };
    });
  }

  /** Send a client message; no-op if the socket is not open. */
  send(msg: ClientMessage): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(encode(msg));
  }

  /** Subscribe to a server message type. Returns an unsubscribe function. */
  on<T extends ServerMessageType>(type: T, handler: Handler<T>): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set<AnyHandler>();
      this.handlers.set(type, set);
    }
    set.add(handler as unknown as AnyHandler);
    return () => this.off(type, handler);
  }

  /** Unsubscribe a previously registered handler. */
  off<T extends ServerMessageType>(type: T, handler: Handler<T>): void {
    const set = this.handlers.get(type);
    if (!set) return;
    set.delete(handler as unknown as AnyHandler);
    if (set.size === 0) this.handlers.delete(type);
  }

  /** Register a callback fired whenever the socket closes. */
  onClose(handler: () => void): void {
    this.closeHandlers.add(handler);
  }

  // -------------------------------------------------------------------------
  // Convenience senders
  // -------------------------------------------------------------------------
  createLobby(name: string, vehicleId: string, token: string): void {
    this.send({ type: 'createLobby', name, vehicleId, token });
  }

  joinLobby(code: string, name: string, vehicleId: string, token: string): void {
    this.send({ type: 'joinLobby', code, name, vehicleId, token });
  }

  leaveLobby(): void {
    this.send({ type: 'leaveLobby' });
  }

  selectVehicle(vehicleId: string): void {
    this.send({ type: 'selectVehicle', vehicleId });
  }

  setReady(ready: boolean): void {
    this.send({ type: 'setReady', ready });
  }

  startRace(): void {
    this.send({ type: 'startRace' });
  }

  sendState(
    p: [number, number, number],
    q: [number, number, number, number],
    v: [number, number, number],
    lap: number,
    cp: number,
  ): void {
    this.send({ type: 'state', p, q, v, lap, cp });
  }

  /** The server times the race itself, so this carries no duration. */
  sendFinished(): void {
    this.send({ type: 'finished' });
  }

  setBots(count: number): void {
    this.send({ type: 'setBots', count });
  }

  setBotSkill(skill: BotSkill): void {
    this.send({ type: 'setBotSkill', skill });
  }

  sendBotStates(bots: BotState[]): void {
    this.send({ type: 'botStates', bots });
  }

  sendBotFinished(id: string): void {
    this.send({ type: 'botFinished', id });
  }

  rematch(): void {
    this.send({ type: 'rematch' });
  }

  selectTrack(trackId: string): void {
    this.send({ type: 'selectTrack', trackId });
  }

  getLeaderboard(trackId: string | null): void {
    this.send({ type: 'getLeaderboard', trackId });
  }

  register(name: string, password: string): void {
    this.send({ type: 'register', name, password });
  }

  login(name: string, password: string): void {
    this.send({ type: 'login', name, password });
  }

  /** Re-establish a sign-in on this socket from a stored session token. */
  resumeSession(session: string): void {
    this.send({ type: 'resumeSession', session });
  }

  logout(): void {
    this.send({ type: 'logout' });
  }

  // -------------------------------------------------------------------------
  // Internal
  // -------------------------------------------------------------------------
  /** Decode a raw frame and dispatch it to registered handlers. */
  private dispatch(raw: string): void {
    let msg: ServerMessage;
    try {
      msg = decode<ServerMessage>(raw);
    } catch {
      // Malformed frame — ignore.
      return;
    }
    if (!msg || typeof msg.type !== 'string') return;

    // Capture our own id before any handler runs.
    if (msg.type === 'joined') {
      this.playerId = msg.playerId;
    }

    const set = this.handlers.get(msg.type);
    if (!set) return;
    // Copy to a snapshot so handlers may unsubscribe during dispatch safely.
    for (const h of Array.from(set)) {
      try {
        h(msg);
      } catch {
        /* swallow handler errors so one bad handler can't break the rest */
      }
    }
  }
}
